import { defineStore } from "pinia";
import { ref } from "vue";
import type { IDomainOutletCoverage } from "~/domain/outlet-coverage";
import { useGuestStore } from "~/stores/guest";

export const useModalHubStore = defineStore("modalHub", () => {
  const guest = useGuestStore();

  const isOrderForLaterWarnOn = ref(false);

  const showOrderForLaterWarn = () => {
    isOrderForLaterWarnOn.value = true;
  };

  const hideOrderForLaterWarn = () => {
    isOrderForLaterWarnOn.value = false;
  };

  const isAddressFormOn = ref(false);
  let addressOnDismiss: (() => void) | undefined;

  const showAddressForm = (onDismiss?: () => void) => {
    isAddressFormOn.value = true;
    addressOnDismiss = onDismiss;
  };

  const hideAddressForm = () => {
    isAddressFormOn.value = false;

    if (addressOnDismiss) {
      addressOnDismiss();
      addressOnDismiss = undefined;
    }
  };

  const refineAddressForm = () => {
    isAddressFormOn.value = false;
  };

  const isAuthManagerOn = ref(false);
  const authAllowProceedAsGuest = ref(false);
  let authManagerOnDismiss: (() => void) | undefined;

  const showAuthManager = (
    onDismiss?: () => void,
    allowProceedAsGuest = false,
  ) => {
    isAuthManagerOn.value = true;
    authManagerOnDismiss = onDismiss;
    authAllowProceedAsGuest.value = allowProceedAsGuest;
  };

  const hideAuthManager = () => {
    isAuthManagerOn.value = false;

    if (authManagerOnDismiss) {
      authManagerOnDismiss();
      authManagerOnDismiss = undefined;

      if (authAllowProceedAsGuest.value) {
        guest.setStartedFlow(false);
      }
    }
  };

  const isDiscountsOn = ref(false);
  let discountOnDismiss: (() => void) | undefined;

  const showDiscounts = (onDismiss?: () => void) => {
    isDiscountsOn.value = true;
    discountOnDismiss = onDismiss;
  };

  const hideDiscounts = () => {
    isDiscountsOn.value = false;

    if (discountOnDismiss) {
      discountOnDismiss();
      discountOnDismiss = undefined;
    }
  };

  const isCrossSellListOn = ref(false);
  let crossSellListOnDismiss: (() => void) | undefined;

  const showCrossSellList = (onDismiss?: () => void) => {
    isCrossSellListOn.value = true;
    crossSellListOnDismiss = onDismiss;
  };

  const hideCrossSellList = () => {
    isCrossSellListOn.value = false;

    if (crossSellListOnDismiss) {
      crossSellListOnDismiss();
      crossSellListOnDismiss = undefined;
    }
  };

  const isLoadingOn = ref(false);
  const loadingTitle = ref<string>("");
  const loadingMessage = ref<string>("");

  const showLoading = (title: string, message: string) => {
    isLoadingOn.value = true;
    loadingTitle.value = title;
    loadingMessage.value = message;
  };

  const hideLoading = () => {
    isLoadingOn.value = false;
    loadingTitle.value = "";
    loadingMessage.value = "";
  };

  const isErrorOn = ref(false);
  const errorTitle = ref<string>("");
  const errorMessage = ref<string>("");
  const errorCtaLabel = ref<string>("");

  const showError = (title: string, message: string, ctaLabel: string = "") => {
    isErrorOn.value = true;
    errorTitle.value = title;
    errorMessage.value = message;
    errorCtaLabel.value = ctaLabel;
  };

  const hideError = () => {
    isErrorOn.value = false;
    errorTitle.value = "";
    errorMessage.value = "";
    errorCtaLabel.value = "";
  };

  const isWarningOn = ref(false);
  const warningTitle = ref<string>("");
  const warningMessage = ref<string>("");
  const warningDismissLabel = ref<string>();
  const warningConfirmLabel = ref<string>();
  const onWarningConfirm = ref<() => void>();
  const onWarningDismiss = ref<() => void>();

  const showWarning = (opts: {
    title: string;
    message: string;
    dismissLabel?: string;
    confirmLabel?: string;
    onWarningConfirm?: () => void;
    onWarningDismiss?: () => void;
  }) => {
    isWarningOn.value = true;
    warningTitle.value = opts.title;
    warningMessage.value = opts.message;
    warningDismissLabel.value = opts.dismissLabel;
    warningConfirmLabel.value = opts.confirmLabel;
    onWarningConfirm.value = opts.onWarningConfirm;
    onWarningDismiss.value = opts.onWarningDismiss;
  };

  const hideWarning = () => {
    if (onWarningDismiss.value) {
      onWarningDismiss.value();
    }

    isWarningOn.value = false;
    warningTitle.value = "";
    warningMessage.value = "";
    warningDismissLabel.value = undefined;
    warningConfirmLabel.value = undefined;
    onWarningConfirm.value = undefined;
    onWarningDismiss.value = undefined;
  };

  const isOutletSelectionOn = ref(false);
  const outletSelectionOutlets = ref<IDomainOutletCoverage[]>([]);
  const isOutletSelectionLoading = ref(false);
  let onOutletSelect:
    | ((outlet: IDomainOutletCoverage) => Promise<void>)
    | undefined;

  const showOutletSelection = (
    outlets: IDomainOutletCoverage[],
    onSelect: (outlet: IDomainOutletCoverage) => Promise<void>,
  ) => {
    isOutletSelectionOn.value = true;
    outletSelectionOutlets.value = outlets;
    onOutletSelect = onSelect;
  };

  const hideOutletSelection = () => {
    isOutletSelectionOn.value = false;
    outletSelectionOutlets.value = [];
    onOutletSelect = undefined;
  };

  const handleOutletSelect = async (outlet: IDomainOutletCoverage) => {
    if (onOutletSelect) {
      try {
        isOutletSelectionLoading.value = true;
        await onOutletSelect(outlet);
        hideOutletSelection();
      } finally {
        isOutletSelectionLoading.value = false;
      }
    }
  };

  return {
    showCrossSellList,
    hideCrossSellList,
    isCrossSellListOn,
    isAuthManagerOn,
    showAuthManager,
    hideAuthManager,
    authAllowProceedAsGuest,
    isDiscountsOn,
    showDiscounts,
    hideDiscounts,
    isLoadingOn,
    showLoading,
    hideLoading,
    loadingTitle,
    loadingMessage,
    isErrorOn,
    showError,
    hideError,
    errorTitle,
    errorMessage,
    errorCtaLabel,
    isAddressFormOn,
    showAddressForm,
    hideAddressForm,
    refineAddressForm,
    showWarning,
    hideWarning,
    isWarningOn,
    warningTitle,
    warningMessage,
    warningDismissLabel,
    warningConfirmLabel,
    onWarningConfirm,
    onWarningDismiss,
    isOrderForLaterWarnOn,
    showOrderForLaterWarn,
    hideOrderForLaterWarn,
    isOutletSelectionOn,
    outletSelectionOutlets,
    isOutletSelectionLoading,
    showOutletSelection,
    hideOutletSelection,
    handleOutletSelect,
  };
});
